import { UnitsApiService } from '@api_patient/units_api.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AsyncSignalValue } from '@component/async.component';
import { asyncSignalAccessible, asyncSignalError, asyncSignalIdle, asyncSignalPending } from '@cortex-shim/async_signal.component';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { signal } from '@preact/signals-react';
import { UnitModel } from '@visiba/api-vcs-client-v2';
import { Injectable, Scopes } from '@visiba/instantiation';

interface ReceptionBrowserPresentationModel {
	rootReceptions: ReceptionBrowserReceptionModel[];
	rootCategories: ReceptionBrowserCategoryModel[];
}

interface ReceptionBrowserUnitModel {
	unitId: number;
	name: string;
	description: string | null;
}

export interface ReceptionBrowserCategoryModel extends ReceptionBrowserUnitModel {
	numberOfListableChildren: number;
	receptions: ReceptionBrowserReceptionModel[];
	childCategories: ReceptionBrowserCategoryModel[];
}

export interface ReceptionBrowserReceptionModel extends ReceptionBrowserUnitModel {
	receptionId: number;
	route: string;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class BrowseReceptionsController {
	public readonly presentation = signal<AsyncSignalValue<ReceptionBrowserPresentationModel>>(asyncSignalIdle());

	constructor(
		private readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
		private readonly userSettingsService: UserSettingsService,
	) {
		// Empty
	}

	public async whenMount() {
		this.presentation.value = asyncSignalPending();

		const result = await this.unitsApiService.getTree(this.universalConfig.rootUnit);
		if (result.err) {
			this.presentation.value = asyncSignalError();
			return;
		}

		const parsed = this.parseBackendData(result.ok.data);
		this.presentation.value = asyncSignalAccessible(parsed);
	}

	private parseBackendData(unit: UnitModel): ReceptionBrowserPresentationModel {
		return {
			// TODO PAT-426: Allow null?
			rootReceptions: this.parseListableReceptions(unit.Children ?? [], ''),
			// TODO PAT-426: Allow null?
			rootCategories: this.parseListableCategories(unit.Children ?? [], ''),
		};
	}

	private parseCategory(unit: UnitModel, numberOfListableChildren: number, parentPath: string): ReceptionBrowserCategoryModel {
		const path = `${parentPath}/${unit.UrlAlias}`;

		return {
			unitId: unit.Id,
			// TODO PAT-426: Allow null?
			name: unit.Name ?? '',
			description: unit.Subtitle,
			numberOfListableChildren: numberOfListableChildren,
			receptions: this.parseListableReceptions(unit.Children ?? [], path),
			childCategories: this.parseListableCategories(unit.Children ?? [], path),
		};
	}

	private parseReception(unit: UnitModel, parentPath: string): ReceptionBrowserReceptionModel {
		return {
			unitId: unit.Id,
			// TODO PAT-426: Allow null?
			receptionId: unit.ReceptionId ?? -1,
			// TODO PAT-426: Allow null?
			name: unit.Name ?? '',
			description: unit.Subtitle,
			route: `${parentPath}/${unit.UrlAlias}`,
		};
	}

	private parseListableReceptions(units: UnitModel[], parentPath: string): ReceptionBrowserReceptionModel[] {
		const filteredUnits = units.filter((unit) => unit.IsClientEntrance && unit.IsListedToClients);
		const parsedReceptions = filteredUnits.map((reception) => this.parseReception(reception, parentPath));

		return this.sortByName(parsedReceptions);
	}

	private parseListableCategories(units: UnitModel[], parentPath: string): ReceptionBrowserCategoryModel[] {
		const filteredUnits = units
			.filter((unit) => !unit.IsClientEntrance)
			.map((unit) => ({ ...unit, numberOfListableChildren: this.countListableChildrenRecursive(unit) }))
			.filter((unit) => unit.numberOfListableChildren > 0);

		const parsedCategories = filteredUnits.map((category) =>
			this.parseCategory(category, category.numberOfListableChildren, parentPath),
		);

		return this.sortByName(parsedCategories);
	}

	private countListableChildrenRecursive(unit: UnitModel): number {
		let count = 0;

		// TODO PAT-426: Allow null?
		const childStack: UnitModel[] = [...(unit.Children ?? [])];
		while (childStack.length > 0) {
			const child = childStack.pop();
			if (child?.IsClientEntrance && child?.IsListedToClients) {
				count++;
			}

			childStack.push(...(child?.Children ?? []));
		}

		return count;
	}

	private sortByName<T extends ReceptionBrowserUnitModel>(items: T[]): T[] {
		const language = this.userSettingsService.getSettings().selectedLanguage;

		return items.sort((a, b) => a.name.localeCompare(b.name, language));
	}
}
